// @ts-nocheck
import React, { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Printer,
  ShieldAlert,
  GitBranch,
  HelpCircle,
  Wrench,
  FileWarning,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatNumber } from "@/lib/utils";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { getStatusConfig } from "@/config/status-registry";
import PrintDialog from "@/components/shared/PrintDialog";
import { EntityLink } from "@/components/shared/EntityLink";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import { useNcrDispositions } from "@/hooks/useNcrDispositions";
import {
  StageDefectSelectorTable,
  normalizeDefectTallyItems,
} from "@/components/quality/StageDefectSelectorTable";
import {
  QcCategorizedMediaSection,
  normalizeCategorizedAttachments,
} from "@/components/quality/QcCategorizedMediaSection";
import { DetailField as F } from "@/components/shared/DetailField";

const SEVERITY: Record<string, { label: string; color: string }> = {
  LOW: { label: "Thấp", color: "bg-slate-50 text-slate-600 border-slate-200" },
  MEDIUM: {
    label: "Trung bình",
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
  HIGH: {
    label: "Cao",
    color: "bg-orange-50 text-orange-700 border-orange-200",
  },
  CRITICAL: {
    label: "Nghiêm trọng",
    color: "bg-rose-50 text-rose-700 border-rose-200",
  },
};

const SOURCE: Record<string, string> = {
  IQC: "IQC - Kiểm tra Vật tư đầu vào",
  IPQC: "IPQC - Kiểm tra Công đoạn SX",
  FQC: "FQC - Nghiệm thu Thành phẩm cuối",
  INTERNAL: "Nội bộ",
  CUSTOMER: "Khiếu nại Khách hàng",
  SUPPLIER: "Nhà cung cấp",
};



export default function NCRDetailPage() {
  const dispositions = useNcrDispositions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ncrId } = useParams({ strict: false }) as { ncrId: string };
  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const {
    data: ncr,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["ncr", ncrId],
    queryFn: async () => (trpcClient as any).ncr.getById.query({ id: ncrId }),
  });

  const handleUpdateStatus = async (newStatus: string) => {
    try {
      await (trpcClient as any).ncr.update.mutate({
        id: ncrId,
        data: { status: newStatus },
      });
      toast.success(`Đã chuyển hồ sơ NCR sang "${getStatusConfig("NCRStatus", newStatus).label || newStatus}"`);
      await queryClient.invalidateQueries({ queryKey: ["ncr", ncrId] });
      await queryClient.invalidateQueries({ queryKey: ["ncr-all"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi cập nhật trạng thái");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await (trpcClient as any).ncr.delete.mutate({ id: ncrId });
      navigate({ to: "/ncr" });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  const d = ncr as any;

  if (!isLoading && !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "NCR", to: "/ncr" }]}
        entityLabel="Phiếu NCR"
        backTo="/ncr"
        codeOrId={ncrId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !d) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "NCR", to: "/ncr" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  const sts = getStatusConfig("NCRStatus", d.status);
  const sev = SEVERITY[d.severity] || SEVERITY.MEDIUM;
  const disp = d.disposition && d.disposition !== "PENDING"
    ? { label: dispositions.labelOf(d.disposition), color: dispositions.colorOf(d.disposition) }
    : null;
  const whyObj = d.whyAnalysis && typeof d.whyAnalysis === "object" ? d.whyAnalysis : {};
  const failedList = Array.isArray(d.failedCriteria) ? d.failedCriteria : [];

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "Phiếu Không Phù Hợp (NCR)", to: "/ncr" }]}
        title={d.ncrId}
        entityId={d.id}
        entityType="ncr"
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        status={
          <Badge variant="outline" className={sts.color}>
            {sts.label || d.status}
          </Badge>
        }
        actions={
          <div className="flex gap-1.5 flex-wrap">
            {d.status === "OPEN" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUpdateStatus("INVESTIGATING")}
                className="text-amber-700 border-amber-300 hover:bg-amber-50 font-semibold"
              >
                Bắt đầu điều tra (D4)
              </Button>
            )}
            {d.status === "INVESTIGATING" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUpdateStatus("CORRECTIVE_ACTION")}
                className="text-blue-700 border-blue-300 hover:bg-blue-50 font-semibold"
              >
                Thực thi CAPA
              </Button>
            )}
            {d.status === "CORRECTIVE_ACTION" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUpdateStatus("VERIFICATION")}
                className="text-indigo-700 border-indigo-300 hover:bg-indigo-50 font-semibold"
              >
                Chuyển thẩm tra
              </Button>
            )}
            {d.status === "VERIFICATION" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUpdateStatus("CLOSED")}
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-bold"
              >
                Đóng Hồ Sơ NCR (D8)
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPrinting(true)}
            >
              <Printer size={14} className="mr-1.5" /> In 8D
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/ncr/$ncrId/edit" as any,
                  params: { ncrId } as any,
                })
              }
            >
              <Pencil size={14} className="mr-1.5" /> Sửa / CAPA
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 size={14} className="mr-1.5" /> Xoá
            </Button>
          </div>
        }
        onBack={() => navigate({ to: "/ncr" })}
        showContextPanel={true}
      >
        <div className="space-y-4">
          {/* ═══ CARD 1: ĐỊNH DANH & TRUY VẾT NGUỒN GỐC (TRACEABILITY FKs) ═══ */}
          <div className="border rounded-lg bg-white shadow-2xs p-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-2 flex-wrap gap-2">
              <h3 className="text-xs font-black uppercase tracking-wider text-rose-900 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-rose-600" /> D1–D2. Mô tả sự không phù hợp
              </h3>
              {disp && (
                <Badge variant="outline" className={`font-bold text-xs whitespace-normal h-auto max-w-full text-left ${disp.color}`}>
                  Hướng xử lý: {disp.label}
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2.5">
              <F label="Số phiếu" value={d.ncrId} mono hl />
              <F label="Nguồn phát hiện" value={SOURCE[d.source] || d.source} />
              <div className="flex gap-2 min-w-0 items-center">
                <span className="text-slate-500 text-xs shrink-0 w-32">Mức độ lỗi</span>
                <Badge variant="outline" className={sev.color}>
                  {sev.label} ({d.severity})
                </Badge>
              </div>

              <F
                label="Chứng từ gốc"
                value={
                  d.relatedEntity ? (
                    <EntityLink
                      entityType={d.relatedEntity.type}
                      id={d.relatedEntity.id}
                      code={d.relatedEntity.code}
                      label={d.relatedEntity.name}
                      variant="badge"
                    />
                  ) : d.relatedId ? (
                    <span className="font-mono text-xs text-indigo-700 font-bold">
                      {d.relatedId}
                    </span>
                  ) : (
                    "—"
                  )
                }
              />

              <F
                label="Lệnh SX"
                value={
                  d.productionOrder ? (
                    <EntityLink
                      entityType="productionOrder"
                      id={d.productionOrder.orderId || d.productionOrder.id}
                      code={d.productionOrder.orderId}
                      label={d.productionOrder.productName}
                      variant="badge"
                    />
                  ) : (
                    d.productionOrderId || "—"
                  )
                }
              />

              <F label="Công đoạn" value={d.processStage || d.routingStep?.name} />

              <F
                label="Máy"
                value={
                  d.machine ? (
                    <EntityLink entityType="machine" id={d.machine.id} code={d.machine.machineId} label={d.machine.name} />
                  ) : null
                }
              />

              <F
                label="Sản phẩm"
                value={
                  d.product ? (
                    <EntityLink
                      entityType="product"
                      id={d.product.id}
                      code={d.product.productCode}
                      label={d.product.productName}
                    />
                  ) : (
                    "—"
                  )
                }
              />

              <F
                label="Vật tư"
                value={
                  d.material ? (
                    <EntityLink entityType="material" id={d.material.id} code={d.material.materialId} label={d.material.name} />
                  ) : null
                }
              />

              <F
                label="Nhà cung cấp"
                value={
                  d.vendor ? <EntityLink entityType="vendor" id={d.vendor.id} code={d.vendor.code} label={d.vendor.name} /> : null
                }
              />

              <F
                label="Số lô / cỡ lô"
                value={`${d.batchNumber ? `Lô ${d.batchNumber} • ` : ""}${formatNumber(d.lotSize || 0)} ${d.unit || "Tờ"}`}
                mono
              />

              <F
                label="SL kiểm / đạt / lỗi"
                value={`Kiểm: ${formatNumber(d.checkedQty || d.sampleSize || 0)} • OK: ${formatNumber(d.okQty ?? Math.max(0, (d.checkedQty || d.sampleSize || 0) - (d.defectQty || 0)))} • NG: ${formatNumber(d.defectQty || 0)} ${d.unit || ""}`}
                mono
              />

              <F label="Bộ phận xử lý" value={d.responsibleDept || "—"} />
              <F
                label="Người phụ trách"
                value={
                  d.owner?.id ? (
                    <EntityLink entityType="user" id={d.owner.userId || d.owner.id} label={d.owner.fullName || d.owner.email} />
                  ) : (
                    "Chưa phân công"
                  )
                }
              />
              <F
                label="Chi phí lỗi"
                value={d.costImpact ? formatMoney(d.costImpact) : "0 ₫"}
                hl
              />
            </div>

            {d.costBreakdown &&
              typeof d.costBreakdown === "object" &&
              (d.costBreakdown.scrapCost || d.costBreakdown.reworkCost || d.costBreakdown.reprintCost) ? (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-100 text-xs">
                <div className="p-2 rounded bg-rose-50/60 border border-rose-200">
                  <span className="text-[10px] text-rose-700 uppercase font-bold block">Hủy hàng</span>
                  <strong className="font-mono text-rose-900">{formatMoney(d.costBreakdown.scrapCost || 0)}</strong>
                </div>
                <div className="p-2 rounded bg-amber-50/60 border border-amber-200">
                  <span className="text-[10px] text-amber-800 uppercase font-bold block">Lựa / sửa lỗi</span>
                  <strong className="font-mono text-amber-950">{formatMoney(d.costBreakdown.reworkCost || 0)}</strong>
                </div>
                <div className="p-2 rounded bg-indigo-50/60 border border-indigo-200">
                  <span className="text-[10px] text-indigo-800 uppercase font-bold block">In bù / phụ phí</span>
                  <strong className="font-mono text-indigo-950">{formatMoney(d.costBreakdown.reprintCost || 0)}</strong>
                </div>
                <div className="p-2 rounded bg-slate-50 border border-slate-200">
                  <span className="text-[10px] text-slate-600 uppercase font-bold block">Ghi chú hạch toán</span>
                  <span className="text-slate-800">{d.costBreakdown.notes || "—"}</span>
                </div>
              </div>
            ) : null}

            <div className="border-t border-slate-100 pt-2">
              <div className="text-xs text-slate-500 font-semibold mb-1">
                Mô tả chi tiết sự không phù hợp (Problem Statement):
              </div>
              <div className="text-sm font-medium text-slate-900 bg-slate-50 p-3 rounded border border-slate-200 whitespace-pre-wrap">
                {d.issueSummary}
              </div>
            </div>

            {normalizeDefectTallyItems(d.defectDetails).length > 0 && (
              <StageDefectSelectorTable
                processStage={d.processStage || (d.source === "IQC" ? "IQC" : d.source === "FQC" ? "FQC" : "PRINTING")}
                lotSize={d.lotSize || 0}
                sampleSize={d.checkedQty || d.sampleSize || 0}
                defectDetails={d.defectDetails}
                onChange={() => {}}
                readOnly
              />
            )}

            {failedList.length > 0 && (
              <div className="pt-2 space-y-2">
                <div className="text-xs font-bold text-rose-800 flex items-center gap-1.5">
                  <FileWarning className="w-4 h-4 text-rose-600" />
                  Các chỉ tiêu kỹ thuật KHÔNG ĐẠT từ biên bản KCS ({failedList.length} chỉ tiêu):
                </div>
                <div className="overflow-x-auto border border-rose-200 rounded">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-rose-50 text-rose-900 text-left">
                        <th className="py-1.5 px-3">Chỉ tiêu</th>
                        <th className="py-1.5 px-3">Tiêu chuẩn</th>
                        <th className="py-1.5 px-3 text-right">Giá trị đo</th>
                        <th className="py-1.5 px-3">Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-rose-100">
                      {failedList.map((fc: any, idx: number) => (
                        <tr key={fc.id || idx}>
                          <td className="py-1.5 px-3 font-semibold text-slate-900">
                            {fc.name}
                          </td>
                          <td className="py-1.5 px-3 font-mono text-slate-600">
                            {fc.standard || "—"}
                          </td>
                          <td className="py-1.5 px-3 text-right font-mono font-bold text-rose-700">
                            {fc.measuredValue ?? "FAIL"} {fc.unit || ""}
                          </td>
                          <td className="py-1.5 px-3 text-slate-500">
                            {fc.note || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ═══ CARD 2: PHÂN TÍCH 6M ISHIKAWA & 5-WHY (D4) ═══ */}
          <div className="border rounded-lg bg-white shadow-2xs p-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-xs font-black uppercase tracking-wider text-indigo-900 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-indigo-600" /> D4. Nguyên nhân gốc
              </h3>
              {d.defectCategory6M && (
                <Badge className="bg-indigo-600 text-white font-mono text-xs">
                  Nhóm 6M: {d.defectCategory6M}
                </Badge>
              )}
            </div>

            {(whyObj.why1 || whyObj.why2 || whyObj.why3 || whyObj.why4 || whyObj.why5) && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
                {[1, 2, 3, 4, 5].map((num) => {
                  const val = whyObj[`why${num}`];
                  if (!val) return null;
                  return (
                    <div
                      key={num}
                      className="p-2.5 bg-indigo-50/50 border border-indigo-200 rounded space-y-1"
                    >
                      <span className="font-mono font-bold text-[10px] text-indigo-700 uppercase block">
                        Tại sao {num} (Why {num})?
                      </span>
                      <p className="text-slate-800 font-medium">{val}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <div>
              <span className="text-xs text-slate-500 font-semibold block mb-1">
                Nguyên nhân gốc:
              </span>
              <p className="text-sm text-slate-800 bg-slate-50 p-3 rounded border border-slate-200 whitespace-pre-wrap">
                {d.rootCause || "Chưa cập nhật nguyên nhân gốc rễ"}
              </p>
            </div>
          </div>

          {/* ═══ CARD 3: HÀNH ĐỘNG NGĂN CHẶN (D3), KHẮC PHỤC (D5) & PHÒNG NGỪA (D7) ═══ */}
          <div className="border rounded-lg bg-white shadow-2xs p-4 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-emerald-900 flex items-center gap-2 border-b pb-2">
              <Wrench className="w-4 h-4 text-emerald-600" /> D3–D7. Ngăn chặn, khắc phục, phòng ngừa
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="p-3 bg-amber-50/50 border border-amber-200 rounded space-y-1">
                <span className="font-bold text-amber-900 uppercase block">
                  D3. Ngăn chặn
                </span>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {d.containmentAction || "—"}
                </p>
              </div>
              <div className="p-3 bg-blue-50/50 border border-blue-200 rounded space-y-1">
                <span className="font-bold text-blue-900 uppercase block">
                  D5. Khắc phục
                </span>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {d.correctiveAction || "—"}
                </p>
              </div>
              <div className="p-3 bg-emerald-50/50 border border-emerald-200 rounded space-y-1">
                <span className="font-bold text-emerald-900 uppercase block">
                  D7. Phòng ngừa
                </span>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {d.preventiveAction || "—"}
                </p>
              </div>
            </div>

            {d.resolutionResult && (
              <div>
                <span className="text-xs text-emerald-800 font-bold block mb-1">
                  D6/D8. Kết quả xử lý:
                </span>
                <p className="text-sm text-emerald-950 bg-emerald-50/60 p-3 rounded border border-emerald-200 whitespace-pre-wrap font-medium">
                  {d.resolutionResult}
                </p>
              </div>
            )}

            {d.capaPlan && (
              <div>
                <span className="text-xs text-slate-500 font-semibold block mb-1">
                  Tổng hợp kế hoạch CAPA:
                </span>
                <p className="text-sm text-slate-800 bg-slate-50 p-3 rounded border border-slate-200 whitespace-pre-wrap">
                  {d.capaPlan}
                </p>
              </div>
            )}
          </div>

          {/* ═══ CARD 4: HÌNH ẢNH HIỆN TRẠNG LỖI & HÌNH ẢNH KẾT QUẢ XỬ LÝ ═══ */}
          {normalizeCategorizedAttachments(d.attachments, "NCR").length > 0 && (
            <QcCategorizedMediaSection
              mode="NCR"
              value={d.attachments}
              onChange={() => {}}
              readOnly
            />
          )}
        </div>
      </StandardDetailLayout>

      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title={`Xoá NCR ${d.ncrId}?`}
        description="Hành động này không thể hoàn tác."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />

      <PrintDialog
        open={isPrinting}
        onClose={() => setIsPrinting(false)}
        type="ncr"
        data={d}
      />
    </>
  );
}
